import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import type { InvoicingSettings } from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Field, Input } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { Toggle } from "../../../core/components/ui/toggle";
import { useToastStore } from "../../../core/stores/toast";
import { getInvoicingSettings, updateInvoicingSettings } from "../lib/api";

const FORM_ID = "atelier-invoicing-settings";

interface InvoicingSettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: (settings: InvoicingSettings) => void;
}

/**
 * Réglages du registre (admin) : délai de paiement et facturation d'office à
 * « Terminé ». L'en-tête des documents (raison sociale, adresse, téléphone,
 * numéros de TPS et de TVQ) se règle dans Réglages › « Factures et devis »,
 * carte en service depuis le 28 octobre 2026 : un seul écran pour ces clés.
 */
export function InvoicingSettingsModal({ open, onClose, onSaved }: InvoicingSettingsModalProps) {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<InvoicingSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(null);
    getInvoicingSettings()
      .then(setDraft)
      .catch(() => push("danger", "Réglages indisponibles"));
  }, [open, push]);

  const set = <K extends keyof InvoicingSettings>(key: K, value: InvoicingSettings[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft || saving) return;
    setSaving(true);
    try {
      const saved = await updateInvoicingSettings({
        paymentTermsDays: draft.paymentTermsDays,
        autoInvoice: draft.autoInvoice,
      });
      push("success", "Réglages enregistrés");
      onSaved?.(saved);
      onClose();
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Réglages de facturation"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={saving || !draft}>
            Enregistrer
          </Button>
        </>
      }
    >
      {!draft ? (
        <p className="empty">Chargement…</p>
      ) : (
        <form id={FORM_ID} className="atelier-form" onSubmit={submit}>
          <Field
            label="Délai de paiement (jours)"
            hint="Échéance d'une facture après son émission ; passé ce délai, elle est « en souffrance »."
          >
            <Input
              type="number"
              min={0}
              max={365}
              value={draft.paymentTermsDays}
              onChange={(e) => set("paymentTermsDays", Math.max(0, Number(e.target.value) || 0))}
            />
          </Field>
          <Toggle
            checked={draft.autoInvoice}
            onChange={(v) => set("autoInvoice", v)}
            label="Facturer d'office quand le travail est terminé"
            hint="La facture est émise au premier passage à « Terminé », « Prêt » ou « Récupéré », s'il y a au moins un service. Sinon, le bouton « Facture » de la fiche de travail l'émet à la main."
          />
          <p className="muted">
            Raison sociale, adresse, téléphone et numéros de TPS et de TVQ imprimés sur les
            factures, devis et états de compte :{" "}
            <Link to="/reglages" onClick={onClose}>
              Réglages › Factures et devis
            </Link>
            .
          </p>
        </form>
      )}
    </Modal>
  );
}
